import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "../../../components/ui";

/** A user who used the app within this many days is considered active. */
export const ACTIVE_WITHIN_DAYS = 30;

const DAY = 24 * 60 * 60 * 1000;

/**
 * App activity from `lastActiveAt`: "active" (seen within ACTIVE_WITHIN_DAYS days),
 * "inactive" (seen before) or "unknown" (never recorded).
 */
export function activityOf(lastActiveAt, now = Date.now()) {
  const time = lastActiveAt ? new Date(lastActiveAt).getTime() : NaN;
  if (!Number.isFinite(time)) return "unknown";
  return now - time <= ACTIVE_WITHIN_DAYS * DAY ? "active" : "inactive";
}

/** "5 minutes ago", "3 days ago"… in the given language. */
export function timeAgo(date, language, now = Date.now()) {
  const seconds = Math.round((new Date(date).getTime() - now) / 1000);
  const units = [
    ["year", 365 * 24 * 3600],
    ["month", 30 * 24 * 3600],
    ["week", 7 * 24 * 3600],
    ["day", 24 * 3600],
    ["hour", 3600],
    ["minute", 60],
  ];
  const format = new Intl.RelativeTimeFormat(language, { numeric: "auto" });
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
  }
  return format.format(0, "minute");
}

/** Account status set by an admin (`status`): enabled or disabled. */
export const AccountStatusBadge = ({ status }) => {
  const { t } = useTranslation();
  return status === true ? (
    <Badge color="green">
      <i className="ri-shield-line" aria-hidden="true"></i> {t("Enabled")}
    </Badge>
  ) : (
    <Badge color="red">
      <i className="ri-forbid-line" aria-hidden="true"></i> {t("Disabled")}
    </Badge>
  );
};

/** App activity (`lastActiveAt`): active / inactive badge with the last activity below. */
export const ActivityBadge = ({ lastActiveAt, withDate = true }) => {
  const { t, i18n } = useTranslation();
  const activity = activityOf(lastActiveAt);
  const badge =
    activity === "active" ? (
      <Badge color="blue" dot>
        {t("Active")}
      </Badge>
    ) : activity === "inactive" ? (
      <Badge color="slate" dot>
        {t("Inactive")}
      </Badge>
    ) : (
      <Badge color="slate">{t("Never seen")}</Badge>
    );

  if (!withDate || activity === "unknown") return badge;
  return (
    <span className="users-activity">
      {badge}
      <span
        className="users-activity-date"
        title={new Date(lastActiveAt).toLocaleString(i18n.language)}
      >
        {timeAgo(lastActiveAt, i18n.language)}
      </span>
    </span>
  );
};
